import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Star, MessageCircle, Check, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useWaMsg } from "@/lib/waTemplates";

const digits = (s) => (s || "").replace(/\D/g, "");

export default function OwnerPostSale() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const shopName = user?.shop?.name || "nossa oficina";
  const reviewUrl = user?.shop?.google_review_url;
  const waMsg = useWaMsg();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/post-sale");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRequested = async (orderId) => {
    setBusy(orderId);
    try {
      await api.post(`/orders/${orderId}/review-requested`);
      toast.success("Marcado como enviado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div>;

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase"><Star className="h-4 w-4" /> Pós-venda</div>
        <h1 className="font-display text-3xl font-black uppercase text-zinc-100 mt-1">Pedir avaliação no Google</h1>
        <p className="text-sm text-zinc-400 mt-1">Ordens prontas que ainda não receberam o pedido de avaliação.</p>
      </div>

      {!reviewUrl && (
        <div className="rounded-xl border border-amber-brand/40 bg-amber-brand/10 p-4 text-sm text-amber-200">
          Para o botão funcionar, cadastre o <strong>link de avaliação do Google</strong> da sua oficina em <Link to="/owner/settings" className="underline inline-flex items-center gap-1"><Settings className="h-3 w-3" /> Configurações</Link>.
        </div>
      )}

      <div className="rounded-2xl border border-card2-border bg-card2 p-4">
        <div className="text-xs font-mono-caps text-zinc-500 uppercase mb-3">Pendentes · {data?.pending?.length || 0}</div>
        {!data?.pending?.length ? (
          <p className="text-sm text-zinc-500">Tudo em dia! Nenhuma avaliação pendente de pedir.</p>
        ) : (
          <ul className="space-y-2">
            {data.pending.map((p) => {
              const d = digits(p.phone);
              const wa = d ? (d.length >= 12 ? d : `55${d}`) : "";
              const msg = encodeURIComponent(waMsg("review", { cliente: p.customer_name || "", placa: p.plate || "", oficina: shopName, link: reviewUrl || "" }));
              const href = wa && reviewUrl ? `https://wa.me/${wa}?text=${msg}` : "";
              return (
                <li key={p.order_id} data-testid={`post-sale-${p.order_id}`} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <Link to={`/owner/ordens/${p.order_id}`} className="text-sm font-bold text-zinc-100 hover:text-green-neon">OS {p.order_number != null ? `#${p.order_number}` : ""} · <span className="font-mono text-xs text-zinc-400">{p.plate}</span></Link>
                    <div className="text-[11px] text-zinc-500">{p.customer_name} · entregue em {p.done_at ? new Date(p.done_at).toLocaleDateString("pt-BR") : "—"}</div>
                    <div data-testid={`post-sale-rating-${p.order_id}`} className="flex items-center gap-0.5 mt-1" title="Avaliação marcada na OS">
                      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= (p.customer_rating || 0) ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} />)}
                      <span className="text-[11px] text-zinc-500 ml-1">{p.customer_rating ? `${p.customer_rating} de 5` : "sem avaliação"}</span>
                    </div>
                    {p.customer_rating_comment && <div data-testid={`post-sale-comment-${p.order_id}`} className="text-[11px] text-zinc-400 italic mt-0.5">“{p.customer_rating_comment}”</div>}
                  </div>
                  <div className="flex items-center gap-1">
                    {href ? (
                      <a data-testid={`post-sale-wa-${p.order_id}`} href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 h-8 px-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase"><MessageCircle className="h-3.5 w-3.5" /> Pedir avaliação</a>
                    ) : (
                      <span className="text-[11px] text-zinc-500">sem telefone ou link</span>
                    )}
                    <Button data-testid={`post-sale-mark-${p.order_id}`} size="sm" variant="outline" disabled={busy === p.order_id} onClick={() => markRequested(p.order_id)} className="h-8 bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800">{busy === p.order_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" /> Enviado</>}</Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div data-testid="post-sale-done" className="rounded-2xl border border-card2-border bg-card2 p-4">
        <div className="text-xs font-mono-caps text-zinc-500 uppercase mb-2">Já pedidas · {data?.requested_count ?? 0}</div>
        <p className="text-xs text-zinc-500">Total de avaliações já pedidas (todos os tempos).</p>
      </div>
    </div>
  );
}
